import React from 'react';
import { AbTest } from '../../models/AbTest';
import { AbTestTypeCategory, AbTestWidgetType, AbTestTypeCategoryToWidgetType } from '../../models/AbTestCreationType';
import { AbTestModalShippingGroups } from './AbTestModalShippingGroups';
import { AbTestModalExtensions } from './AbTestModalExtensions';
import { AbTestModalExtensionGroups } from './AbTestModalExtensionGroups';
import { AbTestModalThemes } from './AbTestModalThemes';
import { AbTestModalCheckoutProfile } from './AbTestModalCheckoutProfile';

interface AbTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  testTypeCategory?: AbTestTypeCategory;
  runningAbTestCheckoutIds: number[];
  hasThemePermission?: boolean;
  onTestTypeChange?: (testType: AbTestTypeCategory) => void;
}

// Export AbTestFormData from shared
export { AbTestFormData } from './shared/abTestModalUtils';

// Helper function to determine widget type (moved outside component to avoid recreation)
function getWidgetType(mode: string, testTypeCategory?: AbTestTypeCategory, existingAbTest?: AbTest): AbTestWidgetType {
  // For create mode, use the provided testTypeCategory
  if (mode == 'create') {
    return AbTestTypeCategoryToWidgetType[testTypeCategory] || AbTestWidgetType.CHECKOUT;
  }

  // For edit/view/duplicate modes, use the testType field from the existing test
  // This fixes the bug where theme tests with checkout groups were incorrectly identified as shipping rates tests
  if (existingAbTest?.testType) {
    const widgetType = AbTestTypeCategoryToWidgetType[existingAbTest.testType];
    if (widgetType) {
      return widgetType;
    }
  }

  // Fallback: infer from first widget type (backwards compatibility)
  const widgetTypeFromVariants = existingAbTest?.abTestVariants?.[0]?.abTestWidgets?.[0]?.type as AbTestWidgetType;
  if (widgetTypeFromVariants) {
    return widgetTypeFromVariants;
  }

  return AbTestWidgetType.CHECKOUT;
}

export const AbTestModal: React.FC<AbTestModalProps> = React.memo(
  ({
    isOpen,
    onClose,
    onSaveSuccess,
    saveTest,
    existingAbTest,
    mode,
    testTypeCategory = AbTestTypeCategory.SHIPPING_RATES,
    runningAbTestCheckoutIds,
    hasThemePermission,
    onTestTypeChange,
  }) => {
    // Determine widget type: use testTypeCategory for create, or saved testType from existingAbTest for edit/view/duplicate
    const widgetType: AbTestWidgetType = getWidgetType(mode, testTypeCategory, existingAbTest);

    // Route to the appropriate modal component based on widget type
    if (widgetType === AbTestWidgetType.EXTENSION) {
      return (
        <AbTestModalExtensions
          isOpen={isOpen}
          onClose={onClose}
          onSaveSuccess={onSaveSuccess}
          saveTest={saveTest}
          existingAbTest={existingAbTest}
          mode={mode}
          onTestTypeChange={onTestTypeChange}
        />
      );
    }

    if (widgetType === AbTestWidgetType.EXTENSION_GROUPS) {
      return (
        <AbTestModalExtensionGroups
          isOpen={isOpen}
          onClose={onClose}
          onSaveSuccess={onSaveSuccess}
          saveTest={saveTest}
          existingAbTest={existingAbTest}
          mode={mode}
          onTestTypeChange={onTestTypeChange}
        />
      );
    }

    if (widgetType === AbTestWidgetType.CHECKOUT_PROFILE) {
      return (
        <AbTestModalCheckoutProfile
          isOpen={isOpen}
          onClose={onClose}
          onSaveSuccess={onSaveSuccess}
          saveTest={saveTest}
          existingAbTest={existingAbTest}
          mode={mode}
          onTestTypeChange={onTestTypeChange}
        />
      );
    }

    // Handle theme tests (CROSS_SITE, PRODUCT_PAGE and SMART_CART)
    if (
      widgetType === AbTestWidgetType.CROSS_SITE ||
      widgetType === AbTestWidgetType.PRODUCT_PAGE ||
      widgetType === AbTestWidgetType.SMART_CART
    ) {
      return (
        <AbTestModalThemes
          isOpen={isOpen}
          onClose={onClose}
          onSaveSuccess={onSaveSuccess}
          saveTest={saveTest}
          existingAbTest={existingAbTest}
          mode={mode}
          runningAbTestCheckoutIds={runningAbTestCheckoutIds}
          testTypeCategory={testTypeCategory}
          hasThemePermission={hasThemePermission}
          onTestTypeChange={onTestTypeChange}
        />
      );
    }

    // Default to checkout modal
    return (
      <AbTestModalShippingGroups
        isOpen={isOpen}
        onClose={onClose}
        onSaveSuccess={onSaveSuccess}
        saveTest={saveTest}
        existingAbTest={existingAbTest}
        mode={mode}
        runningAbTestCheckoutIds={runningAbTestCheckoutIds}
        onTestTypeChange={onTestTypeChange}
      />
    );
  },
);
